import { Theme } from '@mui/material/styles/createTheme';
import makeStyles from '@mui/styles/makeStyles';
import { GridSortModel } from '@mui/x-data-grid';
import React, { useMemo, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useRecoilStateLoadable } from 'recoil';
import { IUserAtom, user } from '../../../recoil/atoms/user';
import {
  endpointEnum,
  makeApiRequestCached,
} from '../../../services/rest/makeAPIRequest';
import { ModalWithStyles } from '../../components/modals/ModalWithStyles';
import dataTableScss from '../../modules/Manage/pages/queues-table/data-table.module.css';
import {
  DataTable,
  IColDef,
} from '../../modules/Manage/pages/queues-table/DataTable';

const useStyles = makeStyles((theme: Theme) => ({
  centerColumn: {
    textAlign: 'center',
  },
  teamLink: {
    paddingInlineStart: theme.spacing(2),
    marginBottom: 0,
  },
  iconRotate: {
    // reload icon animation
    transform: 'rotate(-8100deg)',
    transition: 'all 15s linear',
  },
  fullWidth: {
    /* override DataGrid element style (NOTE: default 100px) */
    width: '100% !important',
    maxWidth: 'none !important',
    minWidth: '150px !important',
  },
}));

// A custom hook that builds on useLocation to parse
// the query string for you.
export function useQueryParams() {
  return new URLSearchParams(useLocation().search);
}

/**
 * Queue editing component
 *
 * @param {Object} props
 * @param {string} [props.id] Queue id
 * @param {boolean} props.show True if modal shown
 * @param {onHideCallback} props.onHide Function to hide modal
 *
 * @see https://github.com/microsoft/vscode/issues/41296
 */
export function BusinessSMSMembersDialog(props: {
  id?: string;
  show: boolean;
  onHide: () => void;
  setUpdatedRowId?: (rowId: string | undefined) => void;
}) {
  const classes = useStyles();

  const [userAtomLoadable, _setUserAtom] = useRecoilStateLoadable<
    IUserAtom | Record<string, unknown>
  >(user);

  const userAtomContents = userAtomLoadable?.contents as IUserAtom;

  type TTeams = {
    teamId: number;
    teamName: string;
  };

  // const businessSMSRule = useMemo(() => {
  //   return props.rules.find((rule) => rule.businessSMSId === props.id);
  // }, [props.rules, props.id]);
  const [_, businessSMSLoadable] = makeApiRequestCached(
    {
      method: 'get',
      // stage: 'itn',
      domain: process.env.VITE_APP_CDK_DOMAIN,
      endpoint:
        endpointEnum.BUSINESS_SMS_BY_BUSINESS +
        `/${userAtomContents?.businessid}` +
        `/${props.id}`,
      payloadJsonString: JSON.stringify({
        apikey: userAtomContents?.apikey,
        businessid: userAtomContents?.businessid,
        businessId: userAtomContents?.businessid,
      }),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.result ?? cached.current
  );

  const businessSMSInboxCached = useRef({} as any);
  businessSMSInboxCached.current = useMemo(
    () =>
      businessSMSLoadable.state === 'hasValue'
        ? {
            id:
              businessSMSLoadable.contents?.response?.data?.data?.businessSMSId,
            ...businessSMSLoadable.contents?.response?.data?.data,
          }
        : businessSMSInboxCached.current,
    [businessSMSLoadable, userAtomContents]
  );

  const businessSMSMembersCached =
    businessSMSInboxCached?.current?.teams?.map((team: TTeams) => {
      return {
        id: team.teamId,
        ...team,
      };
    }) ?? [];

  /**
   * Create column for DataGrid renderer
   * use useMemo to prevent unneeded rerenderings
   */
  const columns = useMemo<IColDef[]>(
    () => [
      {
        field: 'id',
        headerName: 'Team ID',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
      },
      {
        field: 'teamname',
        headerName: 'Team Name',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
        renderCell: (params) =>
          React.useMemo(
            () => (
              <article>
                <div key={params.row.teamId}>
                  <NavLink
                    className="menu-link"
                    to={`/manage/teams/${params.row.teamId}/edit`}
                  >
                    {params.row.teamname}
                  </NavLink>
                </div>
              </article>
            ),
            [params.value, classes]
          ),
      },
      {
        field: 'memberCount',
        headerName: 'Member Count',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
      },
    ],
    [businessSMSMembersCached.current, userAtomContents?.user?.role]
  );

  const [sortModel, setGridSortModel] = useState<GridSortModel>([
    { field: 'id', sort: 'asc' },
  ]);

  return (
    <ModalWithStyles
      title={'Team SMS Rule Members'}
      show={props.show}
      onHide={props.onHide}
      noPaddingDialog={true}
    >
      <DataTable
        sortModel={sortModel}
        setGridSortModel={setGridSortModel}
        rows={businessSMSMembersCached}
        columns={columns}
        loading={businessSMSLoadable.state === 'loading'}
      />
    </ModalWithStyles>
  );
}
